import React, { useState, useEffect } from 'react';
import QRScanner from '../components/attendance/QRScanner';
import { FaQrcode, FaTimesCircle, FaUserCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt
} from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

const ScanQRPage = () => {
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  const markAttendance = async (memberData) => {
    if (!eventId) {
      setError('Please select an event first');
      return;
    }

    try {
      setLoading(true);
      
      // Extract SLIITID from member data
      const SLIITID = memberData.SLIITID || memberData.id || '';
      
      if (!SLIITID) {
        throw new Error('No valid member ID found');
      }
      
      // Mark attendance via the API
      const response = await fetch('/Backend/attendance.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          member_id: SLIITID,
          event_id: eventId,
          status: 'present',
          timestamp: new Date().toISOString()
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark attendance');
      }

      const result = await response.json();
      
      setScanResult({
        success: true,
        SLIITID,
        name: memberData.name || memberData.FullName || 'Member',
        message: 'Attendance marked successfully!'
      });
      
      // Reset the scan result after 5 seconds
      setTimeout(() => setScanResult(null), 5000);
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError(error.message || 'Failed to mark attendance');
      setScanResult({
        success: false,
        message: error.message || 'Failed to mark attendance'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScanComplete = async (qrData) => {
    if (!eventId) {
      setError('Please select an event first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('QR Code Data Received:', qrData);
      
      // Handle case where qrData is a string (raw QR code data)
      if (typeof qrData === 'string') {
        qrData = qrData.trim();
        console.log('Processing string QR data:', qrData);
        
        // Try to parse as JSON if it looks like JSON
        try {
          const parsedData = JSON.parse(qrData);
          console.log('Parsed QR data as JSON:', parsedData);
          qrData = parsedData;
        } catch (e) {
          console.log('QR data is not JSON, treating as raw SLIITID');
          // Not JSON, continue with string processing
        }
      }
      
      // If we have an attendance response object, extract the member data
      if (qrData && qrData.success && qrData.attendance_id) {
        console.log('Received attendance response, extracting member data from QR code');
        // If this is an attendance response, we need to get the member data from the original QR code
        // For now, we'll show a success message since the attendance was already recorded
        setScanResult({
          success: true,
          message: qrData.message || 'Attendance already recorded',
          isAttendanceResponse: true
        });
        return;
      }
      
      // If we have member data (either from the scanner or after parsing)
      if (qrData && (qrData.id || qrData.SLIITID)) {
        console.log('Using provided member data:', qrData);
        setScanResult({
          success: true,
          member: qrData,
          eventId: eventId
        });
        return markAttendance(qrData);
      }
      
      // Handle different data formats
      let SLIITID = '';
      if (typeof qrData === 'string') {
        // Handle raw SLIITID string (with or without prefix)
        SLIITID = qrData.replace(/^(SLIITID:?\s*)/i, '').trim();
        console.log('Extracted SLIITID from string:', SLIITID);
      } else if (qrData && typeof qrData === 'object') {
        // Handle object with SLIITID in various possible locations
        SLIITID = qrData.SLIITID || 
                 qrData.id || 
                 (qrData.member && (qrData.member.SLIITID || qrData.member.id)) ||
                 '';
                 
        if (SLIITID) {
          // Remove any SLIITID: prefix if present
          SLIITID = String(SLIITID).replace(/^(SLIITID:?\s*)/i, '').trim();
          console.log('Extracted SLIITID from object:', SLIITID);
        }
      }
      
      if (!SLIITID) {
        console.error('No valid SLIITID found in QR code data:', qrData);
        throw new Error('No valid SLIITID found in QR code data. Please ensure the QR code contains a valid member ID.');
      }
      
      // Look up the member to get their details
      const memberResponse = await fetch(`/Backend/QRMember.php?SLIITID=${encodeURIComponent(SLIITID)}`);
      
      if (!memberResponse.ok) {
        throw new Error('Failed to verify member');
      }
      
      const memberData = await memberResponse.json();
      
      if (!memberData.success || !memberData.member) {
        throw new Error('Member not found');
      }
      
      // Use the member's internal ID for attendance
      const response = await fetch('/Backend/attendance_api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          member_id: memberData.member.id, // Use internal member ID
          event_id: eventId,
          status: 'present'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark attendance');
      }

      const result = await response.json();
      setScanResult({
        success: true,
        SLIITID,
        name: memberData.member.name,
        message: 'Attendance marked successfully!'
      });
      setError(null);
      
      // Reset the scan result after 5 seconds
      setTimeout(() => setScanResult(null), 5000);
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError(error.message || 'Failed to mark attendance');
      setScanResult({
        success: false,
        message: error.message || 'Failed to mark attendance'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use relative path to the events.php endpoint with a timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`https://dev.sliitleo.org/Backend/events.php?_=${timestamp}`, {
          method: 'GET',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log('API Response:', data); // Debug log
        
        // Check if data is an array (direct response) or has data property
        const eventsData = Array.isArray(data) ? data : (data.data || []);
        
        if (Array.isArray(eventsData)) {
          const formattedEvents = eventsData.map(event => ({
            id: event.id || event.event_id,
            event_name: event.event_name || 'Unnamed Event',
            event_date: event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : '',
            start_time: event.start_time || ''
          })).filter(event => event.id); // Filter out any invalid events
          
          console.log('Formatted Events:', formattedEvents); // Debug log
          setEvents(formattedEvents);
          
          if (formattedEvents.length === 0) {
            setError('No events available. Please create an event first.');
          }
        } else {
          console.error('Unexpected data format:', data);
          throw new Error('Invalid data format received. Expected an array or object with data array.');
        }
      } catch (err) {
        console.error('Error in fetchEvents:', err);
        setError(`Error loading events: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []); // Removed eventId from dependencies to prevent infinite loops
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png',
  };
  return (
    <div className="flex flex-col md:flex-row bg-[#111827] text-white min-h-screen">
        <Sidebar />
                        <Topbar user={user} />
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 shadow-lg">
          <span className="bg-red-600 p-1.5 sm:p-2 rounded-full shadow-lg flex-shrink-0">
            <FaUserCheck className="text-white text-base sm:text-lg" />
          </span>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base md:text-lg font-bold tracking-wide text-white truncate">Attendance Management</h1>
            <p className="text-xs sm:text-sm text-gray-400 truncate">View and manage attendance records</p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
            <Link to="/members" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-blue-900/50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-blue-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-sm sm:text-base"><FontAwesomeIcon icon={faUsers} /></span>
              <span className="truncate">Members</span>
            </Link>
            <Link to="/events" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-orange-400 group">
              <span className="text-orange-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartPie} /></span>
              <span className="truncate">Events</span>
            </Link>
            <Link to="/scan-qr-code" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-blue-400 group">
              <span className="text-blue-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartBar} /></span>
              <span className="truncate">Scan QR</span>
            </Link>
            <Link to="/attendance" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-green-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-green-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-green-400 group">
              <span className="text-white text-sm sm:text-base"><FontAwesomeIcon icon={faListAlt} /></span>
              <span className="truncate">Attendance</span>
            </Link>
          </div>
        </div>

          <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Scan QR Code</h1>
              <p className="text-gray-400">Scan a member's QR code to mark attendance</p>
            </div>
            
            <div className="mt-6">
              <div className="mb-6">
                <label htmlFor="event-select" className="block text-sm font-medium text-gray-200 mb-1">
                  Select Event
                </label>
                <div className="relative">
                  <select
                    id="event-select"
                    value={eventId || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEventId(value === '' ? null : Number(value));
                      // Save the selected event to localStorage
                      if (value) {
                        localStorage.setItem('lastEventId', value);
                      }
                    }}
                    className="block w-full mt-1 pl-3 pr-10 py-2 text-base border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-gray-800 text-white disabled:opacity-50 disabled:bg-gray-800 appearance-none"
                    disabled={loading || events.length === 0}
                  >
                    <option value="">Select an event</option>
                    {loading ? (
                      <option value="" disabled>Loading events...</option>
                    ) : error ? (
                      <option value="" disabled>Error: {error}</option>
                    ) : events.length === 0 ? (
                      <option value="" disabled>No events available</option>
                    ) : (
                      events.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.event_name} - {new Date(event.event_date).toLocaleDateString()}
                          {event.start_time ? ` (${event.start_time.substring(0, 5)})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 p-4 sm:p-6 rounded-xl border border-gray-700">
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <FaTimesCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {scanResult && (
                  <div className={`mb-6 p-4 rounded-md ${scanResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        {scanResult.success ? (
                          <FaQrcode className="h-5 w-5 text-green-400" />
                        ) : (
                          <FaTimesCircle className="h-5 w-5 text-red-400" />
                        )}
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${scanResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {scanResult.message}
                        </p>
                        {scanResult.SLIITID && (
                          <div className="mt-2 space-y-1">
                            <p className="text-sm text-gray-600">Name: {scanResult.name || 'N/A'}</p>
                            <p className="text-sm text-gray-600">SLIIT ID: {scanResult.SLIITID}</p>
                            <p className="text-xs text-gray-500 mt-2">✓ Attendance recorded at {new Date().toLocaleTimeString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <QRScanner 
                  onScanComplete={handleScanComplete} 
                  disabled={!eventId || loading}
                />

                {loading && (
                  <div className="mt-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-400">Processing...</p>
              </div>
                )}
              </div>
            </div>
          </div>
          </main>
          </div>
         
       
  );
};

export default ScanQRPage;
